import * as Dialog from "@radix-ui/react-dialog";
import * as Tabs from "@radix-ui/react-tabs";
import { VirtualTable } from "@/components/reusable";
import { AnimatedDialog } from "@/components/reusable/AnimatedDialog";
import type { ParsedWorksheet } from "@/types";
import { cn } from "@/lib/utils";

interface FilePreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fileName: string;
  worksheets: ParsedWorksheet[];
}

/**
 * Read-only file preview modal accessible from the mapping page.
 * Shows the uploaded file's data in a tabbed virtual table.
 */
export function FilePreviewModal({
  open,
  onOpenChange,
  fileName,
  worksheets,
}: FilePreviewModalProps) {
  return (
    <AnimatedDialog
      open={open}
      onOpenChange={onOpenChange}
      maxWidth="max-w-5xl"
      className="p-6 max-h-[85vh] overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <Dialog.Title className="text-lg font-semibold text-[var(--color-text-primary)]">
            File Preview
          </Dialog.Title>
          <Dialog.Description className="text-sm text-[var(--color-text-secondary)] mt-0.5">
            {fileName}
          </Dialog.Description>
        </div>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-[var(--color-bg-secondary)] text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] transition-colors cursor-pointer"
          aria-label="Close preview"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div>
        {worksheets.length === 0 ? (
          <div className="py-12 text-center text-sm text-[var(--color-text-tertiary)]">
            No file data available to preview.
          </div>
        ) : worksheets.length > 1 ? (
          <Tabs.Root defaultValue={worksheets[0]?.name}>
            <Tabs.List className="flex gap-1 border-b border-[var(--color-border-light)] mb-3">
              {worksheets.map((ws) => (
                <Tabs.Trigger
                  key={ws.name}
                  value={ws.name}
                  className={cn(
                    "px-3 py-1.5 text-xs font-medium rounded-t-md transition-colors border-b-2",
                    "data-[state=active]:border-[var(--color-brand-primary)] data-[state=active]:text-[var(--color-brand-primary)]",
                    "data-[state=inactive]:border-transparent data-[state=inactive]:text-[var(--color-text-tertiary)] data-[state=inactive]:hover:text-[var(--color-text-secondary)]"
                  )}
                >
                  {ws.name}
                  <span className="ml-1.5 text-[10px] opacity-60">({ws.totalRows} rows)</span>
                </Tabs.Trigger>
              ))}
            </Tabs.List>
            {worksheets.map((ws) => (
              <Tabs.Content key={ws.name} value={ws.name}>
                <VirtualTable
                  headers={ws.headers}
                  rows={ws.rows}
                  totalRows={ws.totalRows}
                  maxHeight="calc(85vh - 200px)"
                />
              </Tabs.Content>
            ))}
          </Tabs.Root>
        ) : worksheets[0] ? (
          <>
            <p className="text-xs text-[var(--color-text-tertiary)] mb-2">
              {worksheets[0].totalRows} rows across {worksheets[0].headers.length} columns
            </p>
            <VirtualTable
              headers={worksheets[0].headers}
              rows={worksheets[0].rows}
              totalRows={worksheets[0].totalRows}
              maxHeight="calc(85vh - 180px)"
            />
          </>
        ) : null}
      </div>
    </AnimatedDialog>
  );
}
